import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Compass } from "lucide-react";
import { getPattern } from "@/lib/apiClient";
import { CaseCard } from "@/components/CaseCards";
import { useSite } from "@/context/SiteContext";

function CountBlock({ title, counts, testid }) {
  const entries = Object.entries(counts || {});
  if (entries.length === 0) return null;
  return (
    <div className="border-2 border-ink p-5" data-testid={testid}>
      <p className="eyebrow text-ink/50 mb-3">{title}</p>
      <div className="space-y-1.5">
        {entries.slice(0, 8).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm border-b border-ink/10 pb-1.5">
            <span className="text-ink/80">{k}</span>
            <span className="font-display font-bold">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PatternDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { market } = useSite();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [indFilter, setIndFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  useEffect(() => { getPattern(slug).then(setP).catch(() => setNotFound(true)); }, [slug]);

  const cases = useMemo(() => {
    let cs = p?.cases || [];
    if (indFilter) cs = cs.filter((c) => c.industry === indFilter);
    if (countryFilter) cs = cs.filter((c) => c.country === countryFilter);
    return cs;
  }, [p, indFilter, countryFilter]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="font-display font-extrabold text-3xl">Pattern not found.</h1><Link to="/patterns" className="text-cobalt font-bold mt-4 inline-block">← All patterns</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-20 text-ink/40">Loading…</div>;

  const whyParas = (p.why_interesting || "").split(/\n\n+/).filter(Boolean);

  return (
    <div>
      <section className="border-b-2 border-ink bg-bryellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/patterns" className="group inline-flex items-center gap-2 font-semibold text-sm text-ink/70"><ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> All patterns</Link>
          <h1 data-testid="pattern-detail-title" className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl display-tight uppercase mt-4">{p.name}</h1>
          <div className="flex flex-wrap gap-2 mt-5">
            <span className="eyebrow bg-ink text-white px-3 py-1.5" data-testid="pattern-count-cases">{p.supporting_count} cases</span>
            <span className="eyebrow bg-white border-2 border-ink text-ink px-3 py-1.5">{(p.industries || []).length} industries</span>
            <span className="eyebrow bg-white border-2 border-ink text-ink px-3 py-1.5">{(p.countries || []).length} countries</span>
            {p.source_count ? <span className="eyebrow bg-white border-2 border-ink text-ink px-3 py-1.5">{p.source_count} independent sources</span> : null}
          </div>
          <p className="text-lg text-ink/80 mt-6 max-w-3xl leading-relaxed">{p.description}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {whyParas.length > 0 && (
            <div>
              <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight">Why this is interesting</h2>
              <div className="mt-4 space-y-4 text-ink/80 leading-relaxed">
                {whyParas.map((para, i) => <p key={i}>{para}</p>)}
              </div>
            </div>
          )}

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight">The cases behind this pattern</h2>
            </div>
            <div className="flex flex-wrap gap-3 mt-4 mb-6">
              <select data-testid="pattern-filter-industry" value={indFilter} onChange={(e) => setIndFilter(e.target.value)} className="h-10 px-3 border-2 border-ink bg-white font-semibold text-sm">
                <option value="">All industries</option>
                {(p.industries || []).map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <select data-testid="pattern-filter-country" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="h-10 px-3 border-2 border-ink bg-white font-semibold text-sm">
                <option value="">All countries</option>
                {(p.countries || []).map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-5" data-testid="pattern-cases-grid">
              {cases.map((c) => <CaseCard key={c.slug} c={c} />)}
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="border-2 border-ink bg-cobalt text-white p-6" data-testid="pattern-explore-cta">
            <Compass size={24} />
            <h3 className="font-display font-extrabold text-xl mt-3 leading-tight">Explore this pattern in your market</h3>
            <p className="text-white/80 text-sm mt-2">See whether this same change appears to be happening in {market?.name || "your market"} — real evidence, not predictions.</p>
            <button
              data-testid="pattern-explore-button"
              onClick={() => navigate(`/pattern/${p.slug}/explore`)}
              className="group btn-tactile mt-5 w-full inline-flex items-center justify-center gap-2 bg-white text-ink font-bold h-12 hover:bg-bryellow">
              Explore in {market?.name || "your market"} <ArrowRight size={18} className="arrow-move" />
            </button>
          </div>
          <CountBlock title="Where we're seeing it" counts={p.industry_counts} testid="pattern-where-industries" />
          <CountBlock title="Where it's happening" counts={p.country_counts} testid="pattern-where-countries" />
        </aside>
      </div>
    </div>
  );
}
